import { useCallback } from 'react';
import { InfiniteData, useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import type { ChatbotConversationListResponse, ChatbotConversationRenameResponse } from '@/models/chatbot';
import { chatbotQueryKeys } from './chatbotQueryKeys';
import useChatbotHistoryApi from './useChatbotHistoryApi';

type ListData = InfiniteData<ChatbotConversationListResponse, string | undefined>;

const renameInCache = (data: ListData | undefined, conversationId: string, title: string): ListData | undefined => {
  if (!data) {
    return data;
  }

  return {
    ...data,
    pages: data.pages.map(page => ({
      ...page,
      conversations: page.conversations.map(conversation =>
        conversation.id === conversationId ? { ...conversation, title } : conversation,
      ),
    })),
  };
};

interface UseConversationMutationsReturn {
  rename: UseMutationResult<ChatbotConversationRenameResponse, Error, { id: string; title: string }>;
  remove: UseMutationResult<void, Error, string>;
  commitRename: (id: string, title: string) => void;
}

const useConversationMutations = (): UseConversationMutationsReturn => {
  const queryClient = useQueryClient();
  const { renameConversation, deleteConversation } = useChatbotHistoryApi();

  const invalidateList = useCallback(
    () => queryClient.invalidateQueries({ queryKey: chatbotQueryKeys.conversations }),
    [queryClient],
  );

  const commitRename = useCallback(
    (id: string, title: string) => {
      queryClient.setQueryData<ListData>(chatbotQueryKeys.conversations, current => renameInCache(current, id, title));
    },
    [queryClient],
  );

  const rename = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => renameConversation(id, title),
    onSuccess: invalidateList,
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteConversation(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: chatbotQueryKeys.conversation(id) });
      void invalidateList();
    },
  });

  return { rename, remove, commitRename };
};

export default useConversationMutations;
